import React, { useState, useEffect, useCallback } from 'react';
import axios from '../axios';
import { PlusCircle, X, RefreshCcw } from 'lucide-react';
import { FaTrash, FaSearch, FaFilePdf } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { apiUrl } from '../config/config';

const StojaCheck = () => {
  const { user } = useAuth();
  const [searchType, setSearchType] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [parcelNumber, setParcelNumber] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [buildingNumber, setBuildingNumber] = useState('');
  const [unitPartNumber, setUnitPartNumber] = useState('');
  const [owner, setOwner] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [clients, setClients] = useState([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [properties, setProperties] = useState([]); // Initialize as empty array
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/stojacheck/properties');
      setProperties(response.data.properties || []); // Access the properties array from the response
    } catch (error) {
      console.error('Error fetching properties:', error);
      setError('Failed to fetch properties');
      setProperties([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  const addClient = () => {
    setClients([...clients, { 
      name: '', 
      viewDate: new Date().toISOString().split('T')[0],
      notes: '' 
    }]);
  };

  const removeClient = (index) => {
    const newClients = clients.filter((_, i) => i !== index);
    setClients(newClients);
  };

  const updateClient = (index, field, value) => {
    const newClients = [...clients];
    newClients[index] = { ...newClients[index], [field]: value };
    setClients(newClients);
  };

  const handleAddProperty = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('Dodajanje nepremičnine...');

    try {
      // Step 1: Download PDF
      setIsDownloading(true);
      const downloadResponse = await axios.post('/api/stojacheck/download', {
        searchType,
        propertyType,
        propertyId,
        municipality,
        parcelNumber,
        buildingNumber,
        unitPartNumber,
        rowIndex: properties.length
      });

      if (!downloadResponse.data.success) {
        const errorData = downloadResponse.data;
        console.error('Download error:', errorData);
        throw new Error(errorData.details || errorData.message || 'Napaka pri prenosu PDF-ja');
      }

      const downloadData = downloadResponse.data;
      if (!downloadData.filename) {
        console.error('Invalid download response:', downloadData);
        throw new Error('Napaka pri prenosu PDF-ja: Manjkajoči podatki');
      }

      setStatus('PDF prenesen, analiziram lastništvo...');

      // Step 2: Analyze PDF
      setIsAnalyzing(true);
      const analysisResponse = await axios.post('/api/stojacheck/analyze', {
        pdfFilename: downloadData.filename,
        currentOwner: owner,
        clients,
        rowIndex: properties.length,
        searchType,
        propertyType,
        propertyId,
        municipality,
        parcelNumber,
        buildingNumber,
        unitPartNumber
      });

      if (!analysisResponse.data.success) {
        const errorData = analysisResponse.data;
        throw new Error(errorData.message || 'Napaka pri analizi PDF-ja');
      }

      const analysisData = analysisResponse.data;
      
      // Check if we have valid analysis data
      if (!analysisData.analysis) {
        console.error('Invalid analysis data:', analysisData);
        throw new Error(analysisData.error || 'Napaka pri analizi: Ni veljavnih podatkov');
      }

      // Add property to Google Sheets
      setIsAdding(true);
      const addResponse = await axios.post('/api/stojacheck/add', {
        searchType,
        propertyType,
        propertyId,
        municipality,
        parcelNumber,
        buildingNumber,
        unitPartNumber,
        owner: analysisData.analysis.newOwner || owner,
        clients: clients
          .filter(client => client && client.name && client.name.trim())
          .map(client => ({ name: client.name.trim() })),
        pdfPath: downloadData.filename
      });

      if (!addResponse.data.success) {
        const errorData = addResponse.data;
        throw new Error(errorData.error || 'Napaka pri dodajanju nepremičnine');
      }

      const addData = addResponse.data;
      if (!addData.success) {
        throw new Error(addData.error || 'Napaka pri dodajanju nepremičnine');
      }

      // Update properties with new data
      setProperties(prevProperties => {
        const newProperties = [...prevProperties];
        newProperties[properties.length] = {
          searchType,
          propertyType,
          propertyId,
          municipality,
          parcelNumber,
          buildingNumber,
          unitPartNumber,
          owner: analysisData.analysis.newOwner || owner,
          clients: clients.filter(client => client.name.trim() !== ''),
          status: analysisData.analysis.status || 'V SPREMLJANJU',
          lastChecked: analysisData.analysis.lastChecked || new Date().toLocaleString('sl-SI'),
          pdfPath: downloadData.filename
        };
        return newProperties;
      });

      setStatus(analysisData.analysis.message || 'Preverjanje končano');

      // Clear form
      setSearchType('');
      setParcelNumber('');
      setOwner('');
      setClients([]);
      setBuildingNumber('');
      setUnitPartNumber('');
      setPropertyId('');
      setMunicipality('');
      setPropertyType('');

    } catch (error) {
      console.error('Error adding property:', error);
      setError(error.message);
      setStatus('');
    } finally {
      setIsDownloading(false);
      setIsAnalyzing(false);
      setIsAdding(false);
    }
  };

  const checkProperty = async (property, index) => {
    setError('');
    setStatus('Preverjanje nepremičnine...');

    try {
      // Validate building number for property types 2, 3, 5, and 6
      if (['2', '3', '5', '6'].includes(property.propertyType) && 
          (!property.buildingNumber || isNaN(property.buildingNumber))) {
        throw new Error('Številka stavbe mora biti numerična vrednost');
      }

      // Step 1: Download PDF
      setIsDownloading(true);
      const downloadResponse = await axios.post('/api/stojacheck/download', {
        searchType: property.searchType,
        propertyType: property.propertyType,
        propertyId: property.propertyId,
        municipality: property.municipality,
        parcelNumber: property.parcelNumber,
        buildingNumber: property.buildingNumber,
        unitPartNumber: property.unitPartNumber,
        rowIndex: index
      });

      if (!downloadResponse.data.success) {
        const errorData = downloadResponse.data;
        throw new Error(errorData.message || errorData.error || 'Napaka pri prenosu PDF-ja');
      }

      const downloadData = downloadResponse.data;
      setStatus('PDF prenesen, analiziram lastništvo...');

      // Step 2: Analyze PDF
      setIsAnalyzing(true);
      const analysisResponse = await axios.post('/api/stojacheck/analyze', {
        pdfFilename: downloadData.filename,
        currentOwner: property.owner,
        clients: property.clients,
        rowIndex: index,
        searchType: property.searchType || 'IDZNAK',  // Default to IDZNAK if not specified
        propertyType: property.propertyType || '',
        propertyId: property.propertyId || '',
        municipality: property.municipality || '',
        parcelNumber: property.parcelNumber || '',
        buildingNumber: property.buildingNumber || '',
        unitPartNumber: property.unitPartNumber || ''
      });

      if (!analysisResponse.data.success) {
        const errorData = analysisResponse.data;
        throw new Error(errorData.message || 'Napaka pri analizi PDF-ja');
      }

      const analysisData = analysisResponse.data;
      
      if (!analysisData.analysis) {
        throw new Error('Napaka pri analizi: Ni veljavnih podatkov');
      }

      // Update property in the list
      setProperties(prevProperties => {
        const newProperties = [...prevProperties];
        newProperties[index] = {
          ...property,  // Keep all original property data
          owner: analysisData.analysis.newOwner || property.owner,
          status: analysisData.analysis.status,
          lastChecked: analysisData.analysis.lastChecked,
          pdfPath: downloadData.filename // Add PDF path
        };
        return newProperties;
      });

      setStatus(analysisData.analysis.message || 'Preverjanje končano');

    } catch (error) {
      console.error('Error checking property:', error);
      setError(error.message);
      setStatus('');
    } finally {
      setIsDownloading(false);
      setIsAnalyzing(false);
    }
  };

  const removeProperty = async (index) => {
    try {
      setStatus('Brišem nepremičnino...');
      setError('');

      const response = await axios.delete(`/api/stojacheck/row/${index}`);

      if (!response.data.success) {
        const errorData = response.data;
        throw new Error(errorData.error || 'Napaka pri brisanju');
      }

      // Update local state by removing the property
      setProperties(prevProperties => prevProperties.filter((_, i) => i !== index));
      setStatus('Nepremičnina uspešno izbrisana');
    } catch (error) {
      console.error('Error deleting property:', error);
      setError(error.message || 'Napaka pri brisanju nepremičnine');
    }
  };

  const handleDeleteClick = (index) => {
    setPropertyToDelete(index);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (propertyToDelete !== null) {
      await removeProperty(propertyToDelete);
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPropertyToDelete(null);
  };

  const getFormattedParcelNumber = () => {
    switch (propertyType) {
      case '1': 
        return parcelNumber;
      case '2': 
        return buildingNumber;
      case '3': 
        return '';  
      default:
        return parcelNumber;
    }
  };

  return (
    <div className="container px-2 py-4 mx-auto sm:px-4 sm:py-8">
      <div className="mb-4 text-center sm:mb-8">
        <h1 className="mb-2 text-2xl font-bold text-gray-900 sm:text-3xl">
          Aplikacija za ugotavljanje potencialnih kršitev pogodb
        </h1>
        <h2 className="text-xl font-bold text-red-600 sm:text-2xl">
          STOJACHECK
        </h2>
      </div>
      <div className="p-4 bg-white rounded-lg shadow sm:p-6">
        <div className="p-2 space-y-4 sm:space-y-6 sm:p-6">
          <div className="p-4 bg-white rounded-lg shadow-lg sm:p-6">
            <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-[#D31245]">StojaCheck</h1>

            <div className="space-y-4">
              <div className={`flex ${searchType === 'ID' ? 'flex-row' : 'flex-col'} gap-4`}>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Način vnosa:
                  </label>
                  <select
                    value={searchType}
                    onChange={(e) => {
                      setSearchType(e.target.value);
                      setPropertyId('');
                      setPropertyType('');
                      setMunicipality('');
                      setParcelNumber('');
                      setBuildingNumber('');
                      setUnitPartNumber('');
                      setOwner('');
                      setClients([]);
                    }}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Izberite način vnosa</option>
                    <option value="IDZNAK">Po ID znaku</option>
                    <option value="ID">Po ID</option>
                  </select>
                </div>

                {searchType === 'IDZNAK' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tip nepremičnine</label>
                    <select
                      value={propertyType}
                      onChange={(e) => {
                        setPropertyType(e.target.value);
                        setParcelNumber('');
                        setBuildingNumber('');
                        setUnitPartNumber('');
                      }}
                      className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Izberite tip nepremičnine</option>
                      <option value="1">zemljiška parcela</option>
                      <option value="2">stavba</option>
                      <option value="3">posamezni del stavbe</option>
                    </select>
                  </div>
                )}
              </div>

              {searchType === 'ID' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700">ID nepremičnine</label>
                  <input
                    type="text"
                    value={propertyId}
                    onChange={(e) => setPropertyId(e.target.value)}
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Vnesite ID nepremičnine"
                  />
                </div>
              ) : searchType === 'IDZNAK' ? (
                <>
                  {propertyType && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Šifra K.O.</label>
                      <input
                        type="text"
                        value={municipality}
                        onChange={(e) => setMunicipality(e.target.value)}
                        className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Vnesite šifro katastrske občine"
                      />
                    </div>
                  )}

                  {/* Fields for zemljiška parcela */}
                  {propertyType === '1' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Številka parcele</label>
                      <input
                        type="text"
                        value={parcelNumber}
                        onChange={(e) => setParcelNumber(e.target.value)}
                        className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Vnesite številko parcele"
                      />
                    </div>
                  )}

                  {/* Fields for stavba */}
                  {propertyType === '2' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Številka stavbe</label>
                      <input
                        type="text"
                        value={buildingNumber}
                        onChange={(e) => setBuildingNumber(e.target.value)}
                        className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Vnesite številko stavbe"
                      />
                    </div>
                  )}

                  {/* Fields for posamezni del stavbe */}
                  {propertyType === '3' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Številka stavbe</label>
                        <input
                          type="text"
                          value={buildingNumber}
                          onChange={(e) => setBuildingNumber(e.target.value)}
                          className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Vnesite številko stavbe"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Številka dela stavbe</label>
                        <input
                          type="text"
                          value={unitPartNumber}
                          onChange={(e) => setUnitPartNumber(e.target.value)}
                          className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Vnesite številko dela stavbe"
                        />
                      </div>
                    </>
                  )}
                </>
              ) : null}

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Trenutni lastnik:
                </label>
                <input
                  type="text"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  className="w-full p-2 border rounded-md"
                  placeholder="Vnesite ime in priimek lastnika"
                />
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-medium sm:text-lg">Podatki o strankah</h3>
                  <button
                    type="button"
                    onClick={addClient}
                    className="flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <PlusCircle className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Dodaj stranko</span>
                    <span className="sm:hidden">Dodaj</span>
                  </button>
                </div>

                {clients.map((client, index) => (
                  <div key={index} className="relative p-3 mb-4 rounded-md bg-gray-50 sm:p-4">
                    {clients.length > 1 && (
                      <button
                        onClick={() => removeClient(index)}
                        className="absolute text-gray-500 top-2 right-2 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    
                    <div className="grid grid-cols-1 gap-4 mb-2 sm:grid-cols-2">
                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                          Ime stranke:
                        </label>
                        <input
                          type="text"
                          value={client.name}
                          onChange={(e) => updateClient(index, 'name', e.target.value)}
                          className="w-full p-2 border rounded-md"
                        />
                      </div>
                      
                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                          Datum ogleda:
                        </label>
                        <input
                          type="date"
                          value={client.viewDate}
                          onChange={(e) => updateClient(index, 'viewDate', e.target.value)}
                          className="w-full p-2 border rounded-md"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Opombe:
                      </label>
                      <textarea
                        value={client.notes}
                        onChange={(e) => updateClient(index, 'notes', e.target.value)}
                        className="w-full p-2 border rounded-md"
                        rows="2"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleAddProperty}
                disabled={isLoading || isDownloading || isAnalyzing || isAdding}
                className="w-full bg-[#D31245] text-white py-2 px-4 rounded-md hover:bg-[#B01038] transition-colors disabled:bg-gray-400"
              >
                {isLoading || isDownloading || isAnalyzing || isAdding ? 'Prosim počakajte...' : 'Dodaj in preveri'}
              </button>
            </div>

            {status && (
              <div className="p-2 mt-4 text-sm text-green-700 bg-green-100 rounded-md sm:p-3">
                {status}
              </div>
            )}

            {error && (
              <div className="p-2 mt-4 text-sm text-red-700 bg-red-100 rounded-md sm:p-3">
                {error}
              </div>
            )}
          </div>

          <div className="p-4 bg-white rounded-lg shadow-lg sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold sm:text-xl">Seznam nadzorovanih nepremičnin</h2>
              <div className="flex space-x-4">
                <button
                  onClick={fetchProperties}
                  className="flex items-center text-blue-600 hover:text-blue-800"
                >
                  <RefreshCcw className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Osveži seznam</span>
                </button>
              </div>
            </div>
            
            <div className="flex flex-col mt-4 overflow-hidden sm:mt-8">
              <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-4 lg:px-8">
                  <div className="overflow-hidden border-b border-gray-200 shadow sm:rounded-lg">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-6 sm:py-3">
                              Lastnik
                            </th>
                            <th scope="col" className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-6 sm:py-3">
                              Stranke
                            </th>
                            <th scope="col" className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-6 sm:py-3">
                              Status
                            </th>
                            <th scope="col" className="hidden px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:table-cell">
                              Preverjeno
                            </th>
                            <th scope="col" className="px-3 py-2 text-xs font-medium tracking-wider text-center text-gray-500 uppercase sm:px-6 sm:py-3">
                              PDF
                            </th>
                            <th scope="col" className="px-3 py-2 text-xs font-medium tracking-wider text-center text-gray-500 uppercase sm:px-6 sm:py-3">
                              Akcije
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {properties.map((property, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-xs text-gray-900 sm:px-6 sm:py-4 whitespace-nowrap sm:text-sm">
                                {property.owner}
                              </td>
                              <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-normal text-xs sm:text-sm text-gray-500 max-w-[120px] sm:max-w-none truncate">
                                {Array.isArray(property.clients) ? 
                                  property.clients.map(client => 
                                    typeof client === 'string' ? client : client.name
                                  ).join(', ') : 
                                  ''}
                              </td>
                              <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                  ${property.status === 'KRŠITEV' ? 'bg-red-100 text-red-800' : 
                                    property.status === 'V SPREMLJANJU' ? 'bg-green-100 text-green-800' : 
                                    property.status === 'NI KRŠITVE' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'}`}>
                                  {property.status || 'Ni preverjeno'}
                                </span>
                              </td>
                              <td className="hidden px-6 py-4 text-sm text-gray-500 sm:table-cell whitespace-nowrap">
                                {property.lastChecked || 'Nikoli'}
                              </td>
                              <td className="px-3 py-2 text-center sm:px-6 sm:py-4 whitespace-nowrap">
                                {property.pdfPath && (
                                  <a
                                    className="text-blue-500 hover:text-blue-700"
                                    href={`${apiUrl}/api/stojacheck/pdf/${property.pdfPath.split('\\').pop()}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <FaFilePdf className="inline-block w-4 h-4" />
                                  </a>
                                )}
                              </td>
                              <td className="px-3 py-2 space-x-2 text-xs font-medium text-center sm:px-6 sm:py-4 whitespace-nowrap sm:text-sm">
                                <button
                                  onClick={() => checkProperty(property, index)}
                                  className="px-2 py-1 text-xs font-medium text-white bg-blue-500 rounded hover:bg-blue-700 sm:px-3 sm:text-sm"
                                  disabled={isLoading || isDownloading || isAnalyzing || isAdding}
                                >
                                  <span className="hidden sm:inline">Preveri</span>
                                  <FaSearch className="inline-block w-3 h-3 sm:hidden" />
                                </button>
                                <button
                                  onClick={() => handleDeleteClick(index)}
                                  className="text-red-600 hover:text-red-800"
                                  title="Izbriši"
                                >
                                  <FaTrash className="inline-block w-3 h-3 sm:w-4 sm:h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DeleteConfirmationDialog
            isOpen={deleteDialogOpen}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
          />
        </div>
      </div>
    </div>
  );
};

export default StojaCheck;
